// Content Manager Component
import React, { useState, useEffect } from 'react';
import { Loader2, Edit, Save, X } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContentManager = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchContents(); }, []);

  const fetchContents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/content`, { credentials: 'include' });
      const data = await response.json();
      setContents(data || []);
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (content) => {
    setEditingId(content.id);
    setEditForm({ key: content.key, value_en: content.value_en, value_fr: content.value_fr, value_ar: content.value_ar, section: content.section });
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/content/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchContents();
    } catch (err) {
      console.error('Error saving content:', err);
    }
  };

  const sections = [...new Set(contents.map(c => c.section))];
  const filteredContents = filter === 'all' ? contents : contents.filter(c => c.section === filter);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Contenu du Site</h2>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="all">Toutes les sections</option>
          {sections.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="space-y-4">
        {filteredContents.map((content) => (
          <div key={content.id} className="border border-slate-200 rounded-lg p-4">
            {editingId === content.id ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                  <span className="px-2 py-0.5 bg-slate-100 rounded">{content.section}</span>
                  <span className="font-mono">{content.key}</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 English</label>
                    <textarea value={editForm.value_en} onChange={(e) => setEditForm({...editForm, value_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Français</label>
                    <textarea value={editForm.value_fr} onChange={(e) => setEditForm({...editForm, value_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 العربية</label>
                    <textarea value={editForm.value_ar || ''} onChange={(e) => setEditForm({...editForm, value_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" rows={3} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}><X className="w-4 h-4 mr-1" /> Annuler</Button>
                  <Button size="sm" onClick={() => handleSave(content.id)}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span className="px-2 py-0.5 bg-slate-100 rounded">{content.section}</span>
                    <span className="font-mono">{content.key}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(content)}><Edit className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-slate-400">🇬🇧</span> {content.value_en?.substring(0, 80)}...</div>
                  <div><span className="text-slate-400">🇫🇷</span> {content.value_fr?.substring(0, 80)}...</div>
                  <div className="text-right" dir="rtl"><span className="text-slate-400">🇩🇿</span> {content.value_ar?.substring(0, 80) || '-'}</div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {contents.length === 0 && <div className="text-center py-12 text-slate-500">Aucun contenu.</div>}
    </div>
  );
};

export default ContentManager;
